import React, { useEffect, useState } from 'react';
import { Props } from './Future.types';
import { INPUTS } from 'modules/cm/fields/types';
import { Box, Columns } from 'modules/af_cm_ui_kit/components';
import { VARIANTS_TYPES } from 'modules/af_cm_ui_kit/components/TextField/variants.types';
import { useDispatch, useSelector } from 'react-redux';
import { SummaryBox } from '../../../../../CaseDetailsPage/partials/index';
import formatCurrencyValue from 'utils/formatCurrency';
import { FormFields as fields, validationSchema } from 'modules/cm/form/Applicant/FutureIncome';
import { FIELDS } from 'modules/cm/domain/Case/Applicant/Income/Future/Future';
import Sticky from 'components/Sticky/Sticky';
import { State } from 'modules/cm/store/cases/types';
import UpdatableField from 'modules/cm/fields/UpdatableField';
import { FormProvider, useForm } from 'react-hook-form';
import useYupValidationResolver from '../../../../../../../../form/yup/reactHookFormResolver';
import { updateApplicant } from '../../../../../../store/cases/update/actions';
import useSaveButtonClicked from '../../../../hooks/useSaveButtonClicked';
import { saveCase } from '../../../../../../store/cases/actions';
import * as subPages from '../../../../../../store/subPages';
import { SUBPAGES } from '../../../../../../store/subPages/types';
import CheckDirty from '../../../../../../components/CheckDirty/CheckDirty';

const Future: React.FC<Props> = ({
  modulePrefix,
  clearErrors,
  getErrors,
  applicantIndex,
  data,
  initialData,
  caseId,
  applicantId,
}) => {
  const dispatch = useDispatch();
  const [currentApplicantIndex, setCurrentApplicantIndex] = useState(applicantIndex);

  const methods = useForm<Props['data']>({
    mode: 'onBlur',
    defaultValues: initialData,
    resolver: useYupValidationResolver(validationSchema),
  });

  const { reset } = methods;

  useEffect(() => {
    reset(data, {
      keepDefaultValues: currentApplicantIndex === applicantIndex,
    });
    if (currentApplicantIndex !== applicantIndex) {
      setCurrentApplicantIndex(applicantIndex);
    }
  }, [data, applicantIndex, currentApplicantIndex, reset]);

  const { addListener, removeListener } = useSaveButtonClicked();
  useEffect(() => {
    const listener = addListener(() => {
      dispatch(updateApplicant(methods.getValues(), applicantIndex));
      dispatch(saveCase());
    });
    return () => {
      removeListener(listener);
      dispatch(updateApplicant(methods.getValues(), applicantIndex));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantIndex]);

  const futureIncomeReadonly = useSelector(({ currentCase }: { currentCase: State }) => {
    return currentCase.readonlyFields.applicants[applicantIndex].futureIncome;
  });

  const getSummaryErrorMessage = (fieldName: string) => getErrors(fieldName).join(' ');

  return (
    <>
      <Sticky top={202}>
        <SummaryBox
          data={[
            {
              title: 'Retired income total',
              value:
                futureIncomeReadonly[FIELDS.MINIMUM_RETIRED_INCOME] !== null
                  ? formatCurrencyValue(futureIncomeReadonly[FIELDS.MINIMUM_RETIRED_INCOME] as NonNullable<number>)
                  : 'N/A',
              errorMessage: getSummaryErrorMessage(FIELDS.MINIMUM_RETIRED_INCOME),
            },
            {
              title: 'Pension total',
              value:
                futureIncomeReadonly[FIELDS.FUTURE_INCOME_PENSION] !== null
                  ? formatCurrencyValue(futureIncomeReadonly[FIELDS.FUTURE_INCOME_PENSION] as NonNullable<number>)
                  : 'N/A',
              errorMessage: getSummaryErrorMessage(FIELDS.FUTURE_INCOME_PENSION),
            },
            {
              title: 'Total',
              value:
                futureIncomeReadonly[FIELDS.FUTURE_INCOME_TOTAL] !== null
                  ? formatCurrencyValue(futureIncomeReadonly[FIELDS.FUTURE_INCOME_TOTAL] as NonNullable<number>)
                  : 'N/A',
              errorMessage: getSummaryErrorMessage(FIELDS.FUTURE_INCOME_TOTAL),
            },
          ]}
        />
      </Sticky>

      <FormProvider<Props['data']> {...methods}>
        <CheckDirty
          initialData={methods.getValues()}
          onDirtyChange={(isDirty) => {
            dispatch(
              subPages.actions.updateApplicantSubPageIsDirty(applicantIndex, SUBPAGES.APPLICANT_INCOME_FUTURE, isDirty),
            );
          }}
        />
        <form onSubmit={() => {}}>
          <>
            <Columns spacing={4}>
              <>
                <Box title="Employed Income">
                  <UpdatableField
                    modulePrefix={modulePrefix}
                    fieldType={INPUTS.TEXT}
                    field={fields[FIELDS.EMPLOYED_ANTICIPATED_GROSS_ANNUAL_INCOME]}
                    variant={VARIANTS_TYPES.CURRENCY}
                    getErrors={getErrors}
                  />
                  <UpdatableField
                    modulePrefix={modulePrefix}
                    fieldType={INPUTS.TEXTAREA}
                    field={fields[FIELDS.EMPLOYED_ANTICIPATED_RETIREMENT_DATE]}
                    variant={VARIANTS_TYPES.TEXT}
                    getErrors={getErrors}
                  />
                </Box>

                <Box title="Minimum retired Income">
                  <UpdatableField
                    modulePrefix={modulePrefix}
                    fieldType={INPUTS.TEXT}
                    field={fields[FIELDS.MINIMUM_TAXABLE_RETIRED_INCOME]}
                    variant={VARIANTS_TYPES.CURRENCY}
                    getErrors={getErrors}
                  />
                  <UpdatableField
                    modulePrefix={modulePrefix}
                    fieldType={INPUTS.TEXTAREA}
                    field={fields[FIELDS.MINIMUM_TAXABLE_RETIRED_INCOME_DETAILS]}
                    variant={VARIANTS_TYPES.TEXT}
                    getErrors={getErrors}
                  />
                  <UpdatableField
                    modulePrefix={modulePrefix}
                    fieldType={INPUTS.CHECKBOX}
                    field={fields[FIELDS.HAS_TAXABLE_RETIRED_INCOME_CONTINUING]}
                    variant="inline"
                    getErrors={getErrors}
                  />
                  <UpdatableField
                    modulePrefix={modulePrefix}
                    fieldType={INPUTS.TEXT}
                    field={fields[FIELDS.MINIMUM_TAX_FREE_RETIRED_INCOME]}
                    variant={VARIANTS_TYPES.CURRENCY}
                    getErrors={getErrors}
                  />
                  <UpdatableField
                    modulePrefix={modulePrefix}
                    fieldType={INPUTS.TEXTAREA}
                    field={fields[FIELDS.MINIMUM_TAX_FREE_RETIRED_INCOME_DETAILS]}
                    variant={VARIANTS_TYPES.TEXT}
                    getErrors={getErrors}
                  />
                  <UpdatableField
                    modulePrefix={modulePrefix}
                    fieldType={INPUTS.CHECKBOX}
                    field={fields[FIELDS.HAS_TAX_FREE_RETIRED_INCOME_CONTINUING]}
                    variant="inline"
                    getErrors={getErrors}
                  />
                </Box>
              </>
              <>
                <Box title="Self-employed Income">
                  <UpdatableField
                    modulePrefix={modulePrefix}
                    fieldType={INPUTS.TEXT}
                    field={fields[FIELDS.SELF_EMPLOYED_ANTICIPATED_GROSS_ANNUAL_INCOME]}
                    variant={VARIANTS_TYPES.CURRENCY}
                    getErrors={getErrors}
                  />
                  <UpdatableField
                    modulePrefix={modulePrefix}
                    fieldType={INPUTS.TEXTAREA}
                    field={fields[FIELDS.SELF_EMPLOYED_ANTICIPATED_RETIREMENT_DATE]}
                    variant={VARIANTS_TYPES.TEXT}
                    getErrors={getErrors}
                  />
                </Box>
                <Box title="Other Income">
                  <UpdatableField
                    modulePrefix={modulePrefix}
                    fieldType={INPUTS.TEXT}
                    field={fields[FIELDS.OTHER_INCOME_ANTICIPATED_GROSS_ANNUAL_INCOME]}
                    variant={VARIANTS_TYPES.CURRENCY}
                    getErrors={getErrors}
                  />
                  <UpdatableField
                    modulePrefix={modulePrefix}
                    fieldType={INPUTS.TEXTAREA}
                    field={fields[FIELDS.OTHER_INCOME_DETAILS]}
                    variant={VARIANTS_TYPES.TEXT}
                    getErrors={getErrors}
                  />
                </Box>
              </>
            </Columns>
          </>
        </form>
      </FormProvider>
    </>
  );
};

export default Future;
